/**
 * Lightweight, first-party conversion tracking.
 *
 * Events are posted to `/api/public/conversions`, which validates and stores
 * them in the backend. No third-party script, no cookies — a session id is
 * kept in sessionStorage only so repeat CTA clicks in one visit can be
 * de-duplicated in reporting.
 */

export const CONVERSION_EVENTS = [
  "lead_submit",
  "cta_click",
  "call_click",
  "email_click",
  "form_start",
  "audit_request",
  "resource_download",
] as const;

export type ConversionEvent = (typeof CONVERSION_EVENTS)[number];

const SESSION_KEY = "op_conv_sid";

function sessionId(): string | undefined {
  if (typeof sessionStorage === "undefined") return undefined;
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

function device(): string {
  if (typeof window === "undefined") return "unknown";
  return window.innerWidth < 768 ? "mobile" : "desktop";
}

export function trackConversion(
  event: ConversionEvent,
  opts: { label?: string; value?: number; path?: string } = {},
): void {
  if (typeof window === "undefined") return;
  const body = JSON.stringify({
    event,
    path: (opts.path ?? window.location.pathname).slice(0, 512),
    label: opts.label ? opts.label.slice(0, 160) : undefined,
    value: typeof opts.value === "number" ? opts.value : undefined,
    device: device(),
    session_id: sessionId(),
    referrer: document.referrer ? document.referrer.slice(0, 512) : undefined,
  });

  try {
    // sendBeacon survives the navigation a CTA click triggers.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        "/api/public/conversions",
        new Blob([body], { type: "application/json" }),
      );
      return;
    }
  } catch {
    /* fall through to fetch */
  }

  void fetch("/api/public/conversions", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}
